import React from 'react';

import { computed, observable, makeAutoObservable } from 'mobx';

import { Button } from 'src/domains/common/components/button/button';

import { Common } from 'src/AppState/Common';
import { ToasterState } from 'src/AppState/ToasterState';

import { HtmlElementReactive } from 'src_common/common/mobx-utils/HtmlElementReactive';

export class CSVUploadState {
    @observable public isLoading: boolean = false;
    @observable public file: File | null = null;
    @observable public fileInputRef: HtmlElementReactive<HTMLElement>;
    @observable public errors: string[] = [];

    public constructor(private readonly common: Common) {
        this.fileInputRef = new HtmlElementReactive(500);
        makeAutoObservable(this);
    }

    @computed public get fileName(): string {
        if (this.file === null) {
            return '';
        }

        return this.file.name;
    }

    public setFileRef = (element: HTMLInputElement | null): void => {
        this.fileInputRef.setRef(element);
    };

    public onSelectFile = (event: React.ChangeEvent<HTMLInputElement>): void => {
        this.errors = [];
        const target = event.target;

        if (target instanceof HTMLInputElement) {
            if (target.files !== null) {
                const file = target.files[0];

                if (file === undefined) {
                    this.errors = ['Problem receiving a file'];
                } else {
                    this.file = file;
                }
            }
        }
    };

    public onFileDrop = (event: React.DragEvent<HTMLLabelElement>): void => {
        this.errors = [];

        event.preventDefault();
        const droppedFiles = event.dataTransfer.files;
        if (droppedFiles.length > 0) {
            const newFiles = Array.from(droppedFiles);
            const validFileTypes = [
                'text/csv',
                'text/x-csv',
                'application/x-csv',
                'application/csv',
                'text/x-comma-separated-values',
                'text/comma-separated-values',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
            ];

            if (newFiles[0] !== undefined && validFileTypes.includes(newFiles[0].type)) {
                this.file = newFiles[0];
            } else {
                this.errors = ['Invalid file type'];
                ToasterState.get(this.common).setToast({
                    variant: 'error',
                    title: 'Upload Error',
                    message: 'Invalid file type, please use .csv or .xlsx',
                    button: (
                        <Button
                            variant='outline'
                            onClick={() => this.fileInputRef.ref?.click()}
                        >
                            Upload another CSV
                        </Button>
                    ),
                });
            }
        } else {
            this.errors = ['Problem receiving a file'];
        }
    };

    public upload = async (): Promise<void> => {
        this.errors = [];

        if (this.file === null) {
            ToasterState.get(this.common).setToast({
                variant: 'error',
                title: 'Upload Error',
                button: (
                    <Button
                        variant='outline'
                        onClick={() => this.fileInputRef.ref?.click()}
                    >
                        Upload another CSV
                    </Button>
                ),
            });

            this.errors = ['Upload - problem with uploading a file to the server, no file selected'];
        } else {
            this.isLoading = true;

            try {
                const response = await this.common.trpcClient.csvUpload(this.file);

                if (response.type === 'ok') {
                    ToasterState.get(this.common).setToast({
                        variant: 'success',
                        title: 'Upload Successful',
                        button: (
                            <Button
                                variant='outline'
                                onClick={() => this.fileInputRef.ref?.click()}
                            >
                                Upload another CSV
                            </Button>
                        ),
                    });

                    this.isLoading = false;
                    this.file = null;
                } else {
                    ToasterState.get(this.common).setToast({
                        variant: 'error',
                        title: 'Upload Error',
                        message: response.body,
                        button: (
                            <Button
                                variant='outline'
                                onClick={() => this.fileInputRef.ref?.click()}
                            >
                                Upload another CSV
                            </Button>
                        ),
                    });
                    this.errors = ['Error uploading the file'];
                    this.isLoading = false;
                }
            } catch (error) {
                console.error(error);
                this.errors = ['Error uploading the file'];
                this.isLoading = false;
            }
        }
    };
}
